import axios from 'axios'

const API_URL = 'https://backend.nexgridco.com'
const GOOGLE_CLIENT_ID = '889882705544-he170r6n8p8mril29vfuashjrpb521k2.apps.googleusercontent.com'
const AUTH_PATHS = ['/auth/login', '/auth/signup', '/auth/google', '/auth/me', '/auth/password']

function base64UrlDecode(str) {
  try {
    const b64 = str.replace(/-/g, '+').replace(/_/g, '/')
    const pad = b64.length % 4 ? '='.repeat(4 - (b64.length % 4)) : ''
    return decodeURIComponent(
      atob(b64 + pad)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    )
  } catch {
    return '{}'
  }
}

function decodeJwt(token) {
  try {
    const payload = token.split('.')[1] || ''
    return JSON.parse(base64UrlDecode(payload))
  } catch {
    return {}
  }
}

class AuthService {
  constructor() {
    axios.defaults.baseURL = API_URL
    this._logoutTimer = null
    this.installAxiosInterceptor()
    this.scheduleLogoutFromStoredToken()
  }

  isAuthPath(url = '') {
    try {
      const u = new URL(url, axios.defaults.baseURL)
      return AUTH_PATHS.some(p => u.pathname.startsWith(p))
    } catch {
      return AUTH_PATHS.some(p => String(url || '').startsWith(p))
    }
  }

  installAxiosInterceptor() {
    if (this._axiosInterceptorInstalled) return

    axios.interceptors.request.use(
      (config) => {
        const storage = this.storageWithToken()
        const token = storage.getItem('token')
        if (token && !this.isTokenExpired(token)) {
          config.headers = config.headers || {}
          config.headers.Authorization = `Bearer ${token}`
        }
        return config
      },
      (error) => Promise.reject(error)
    )

    axios.interceptors.response.use(
      (res) => res,
      (err) => {
        const status = err?.response?.status
        const reqUrl = err?.config?.url || ''
        if (status === 401 && !this.isAuthPath(reqUrl)) {
          this.logout()
          if (typeof window !== 'undefined') window.location.href = '/'
        }
        return Promise.reject(err)
      }
    )

    this._axiosInterceptorInstalled = true
  }

  scheduleLogoutFromStoredToken() {
    const storage = this.storageWithToken()
    const token = storage.getItem('token')
    if (!token) return
    const { exp } = decodeJwt(token)
    if (!exp) return
    const delay = exp * 1000 - Date.now()
    if (delay <= 0) {
      this.logout()
      if (typeof window !== 'undefined') window.location.href = '/'
      return
    }
    this.clearLogoutTimer()
    this._logoutTimer = setTimeout(() => {
      this.logout()
      if (typeof window !== 'undefined') window.location.href = '/'
    }, delay)
  }

  clearLogoutTimer() {
    if (this._logoutTimer) {
      clearTimeout(this._logoutTimer)
      this._logoutTimer = null
    }
  }

  handleLoginSuccess(data, storage = localStorage) {
    const normalizedRole = String(data.role || '').startsWith('ROLE_') ? data.role : `ROLE_${data.role}`
    storage.setItem('token', data.token)
    storage.setItem('usuario_id', data.usuario_id || '')
    storage.setItem('role', normalizedRole || '')
    if (data.user) storage.setItem('user', JSON.stringify(data.user))
    const { exp } = decodeJwt(data.token)
    if (exp) {
      const delay = exp * 1000 - Date.now()
      if (delay > 0) {
        this.clearLogoutTimer()
        this._logoutTimer = setTimeout(() => {
          this.logout()
          if (typeof window !== 'undefined') window.location.href = '/'
        }, delay)
      } else {
        this.logout()
        if (typeof window !== 'undefined') window.location.href = '/'
      }
    }
  }

  isTokenExpired(token) {
    const { exp } = decodeJwt(token)
    if (!exp) return false
    return Date.now() >= exp * 1000
  }

  storageWithToken() {
    return localStorage.getItem('token') ? localStorage : sessionStorage
  }

  current() {
    const storage = this.storageWithToken()
    const token = storage.getItem('token')
    const usuario_id = storage.getItem('usuario_id')
    const role = storage.getItem('role')
    const user = storage.getItem('user') ? JSON.parse(storage.getItem('user')) : null
    if (!token || !usuario_id) return null
    if (this.isTokenExpired(token)) {
      this.logout()
      return null
    }
    return { token, usuario_id, role, user }
  }

  authHeaders() {
    const storage = this.storageWithToken()
    const token = storage.getItem('token')
    if (token && !this.isTokenExpired(token)) {
      return { Authorization: `Bearer ${token}` }
    }
    return {}
  }

  async login({ email, password }, remember = true) {
    const { data } = await axios.post(`/auth/login`, { email, password })
    this.handleLoginSuccess(data, remember ? localStorage : sessionStorage)
    return data
  }

  async signup({ name, email, password }) {
    const { data } = await axios.post(`/auth/signup`, { name, email, password })
    this.handleLoginSuccess(data)
    return data
  }

  async loginWithGoogleIdToken(idToken, remember = true) {
    const { data } = await axios.post(`/auth/google`, { idToken })
    this.handleLoginSuccess(data, remember ? localStorage : sessionStorage)
    return data
  }

  async me() {
    const headers = this.authHeaders()
    if (!headers.Authorization) return null
    try {
      const { data } = await axios.get(`/auth/me`, { headers })
      return data
    } catch {
      this.logout()
      return null
    }
  }

  async requestPasswordReset(email) {
    await axios.post(`/auth/password/forgot`, { email })
  }

  async resetPassword({ token, password }) {
    await axios.post(`/auth/password/reset`, { token, password })
  }

  logout() {
    this.clearLogoutTimer()
    localStorage.clear()
    sessionStorage.clear()
  }

  getGoogleClientId() {
    return GOOGLE_CLIENT_ID
  }
}

export default new AuthService()
